import { z } from "zod/v4"

import type { AiImageSpec } from "./schema"
import { AI_IMAGE_PLACEHOLDER_TAG, aiImageSpecSchema } from "./schema"

const persistedImagesSchema = z.object({
	images: z.record(z.string(), aiImageSpecSchema),
})

// Reopening a chat has to bring the screenshots back with the answer that
// referenced them, or its placeholders render as nothing.
export function readPersistedImages(
	enrichments: unknown,
): Record<string, AiImageSpec> | undefined {
	const parsed = persistedImagesSchema.safeParse(enrichments)
	if (!parsed.success) return undefined
	return Object.keys(parsed.data.images).length > 0
		? parsed.data.images
		: undefined
}

const PLACEHOLDER_RE = new RegExp(
	`<${AI_IMAGE_PLACEHOLDER_TAG}\\b[^>]*>(?:\\s*</${AI_IMAGE_PLACEHOLDER_TAG}>)?`,
	"gi",
)

const ID_RE = /\bdata-id\s*=\s*"([^"]*)"/i

export type AiHtmlSegment =
	| { kind: "html"; html: string }
	| { kind: "image"; id: string }

function escapeAttribute(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
}

// Splits an answer body into the HTML around each <pl-image> and the ids they
// carry, so a renderer can drop a React component between two innerHTML chunks.
export function splitAiHtmlByImagePlaceholders(
	html: string,
	images?: Record<string, AiImageSpec>,
): AiHtmlSegment[] {
	// A turn that only fetched a screenshot has no text, and the screenshot
	// still has to show.
	if (!html) return trailingImageSegments([], images)

	const segments: AiHtmlSegment[] = []
	let cursor = 0

	PLACEHOLDER_RE.lastIndex = 0
	let match: RegExpExecArray | null
	while ((match = PLACEHOLDER_RE.exec(html))) {
		const id = ID_RE.exec(match[0])?.[1]?.trim()
		const before = html.slice(cursor, match.index)
		if (before) segments.push({ kind: "html", html: before })
		// A placeholder with no spec renders nothing, so it is dropped along with
		// its markup rather than left in the DOM as an unknown element.
		if (id && images?.[id]) segments.push({ kind: "image", id })
		cursor = match.index + match[0].length
	}

	const rest = html.slice(cursor)
	if (rest) segments.push({ kind: "html", html: rest })

	return [...segments, ...trailingImageSegments(segments, images)]
}

// The model prepares a screenshot and then sometimes writes its answer without
// the tag, which would drop the picture it just fetched. An answer that placed
// no tag at all gets them appended; one that placed a tag keeps its own choice
// of which screenshots to show.
function trailingImageSegments(
	segments: AiHtmlSegment[],
	images?: Record<string, AiImageSpec>,
): AiHtmlSegment[] {
	if (!images || segments.some((segment) => segment.kind === "image")) return []
	return Object.keys(images).map((id) => ({ kind: "image" as const, id }))
}

export function stripAiImagePlaceholders(html: string): string {
	return html.replace(PLACEHOLDER_RE, "")
}

// Surfaces that cannot run our SVG overlay (email, inbox thread HTML) still get
// the screenshot, just without the marks drawn on it.
export function flattenAiImagePlaceholders(
	html: string,
	images?: Record<string, AiImageSpec>,
): string {
	const shown = new Set<string>()
	const imgTag = (spec: AiImageSpec) =>
		`<img src="${escapeAttribute(spec.src)}" alt="${escapeAttribute(spec.alt)}" />`

	const body = html.replace(PLACEHOLDER_RE, (tag) => {
		const id = ID_RE.exec(tag)?.[1]?.trim()
		const spec = id ? images?.[id] : undefined
		if (!spec || !id) return ""
		shown.add(id)
		return imgTag(spec)
	})

	const trailing =
		shown.size > 0
			? ""
			: Object.values(images ?? {})
					.map(imgTag)
					.join("")

	return body + trailing
}
